import { Formations } from "@/api/privateRequests/squad-builder/get-formations";
import Button from "@/components/Button";
import { trackExplore } from "@/events/squadBuilderMixpanel";
import { cn } from "@/lib/utils";
import { getFormationsLabel } from "@/redux/loadedContent/loadedContentSlice";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";

import { APIStatus } from "../../../../../api/APIStatus.type";
import { addAlert } from "../../../../../redux/message/messageSlice";
import { updateTacticInsertStatus as sbUpdateTacticInsertStatus } from "../../../../../redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { AppDispatch } from "../../../../../redux/store";
import {
  Tactic,
  updateTacticInsertStatus as tacticsUpdateTacticInsertStatus,
} from "../../../../../redux/tactics/tacticsSlice";
import { InputField } from "../../../../molecules/InputField/InputField";
import { TacticInfo } from "../../../Tactics/TacticInfo";
import { TacticPlayerRoles } from "../../../Tactics/TacticPlayerRoles";
import { TacticalPreset } from "../../../Tactics/TacticalPreset";
import { PanelHeader } from "../../RightPanel/PanelHeader/PanelHeader";
import { PanelContainer } from "../../components/PanelContainer/PanelContainer";

type Props = {
  mode: "squad-builder" | "tactics-overview";
  onClose?: () => void;
  chosenTactic?: Tactic;
  handleUseTacticInSquadBuilder?: (chosenTactic: Tactic) => void;
  handleCopy?: () => void;
  handleChangeFormation?: (formationId: string) => void;
  handleChangeBuildUpStyle?: (buildUpStyle: string) => void;
  handleChangeDefensiveApproach?: (defensiveApproach: string) => void;
  handleChangeLineHeight?: (lineHeight: number) => void;
  handleGetTacticFromCode?: (code: string) => void;
  statusInsertTacticCode?: APIStatus;
  desktopMode?: boolean;
  canUploadTactic?: boolean;
  handleUploadTacticsClicked?: () => void;
  formations: Formations;
};

type InsertCodeAndApplyProps = {
  eaCode: string;
  setEaCode: (code: string) => void;
  tacticErrorMessage: string;
  setTacticErrorMessage: (message: string) => void;
  handleGetTacticFromCode: (code: string) => void;
  updateTacticStatus: () => void;
};

export const InsertCodeAndApply: React.FC<InsertCodeAndApplyProps> = ({
  eaCode,
  setEaCode,
  tacticErrorMessage,
  setTacticErrorMessage,
  handleGetTacticFromCode,
  updateTacticStatus,
}) => {
  const { t } = useTranslation(["squadBuilder", "common"]);
  const onSubmit = () => {
    if (eaCode.length !== 12 && eaCode.length !== 11) {
      setTacticErrorMessage(t("tactic.invalidCodeEntry"));
      return;
    }
    handleGetTacticFromCode(eaCode);
  };
  return (
    <>
      <InputField
        value={eaCode}
        onChange={(code) => {
          if (tacticErrorMessage) {
            setTacticErrorMessage("");
            updateTacticStatus();
          }
          setEaCode(code);
        }}
        onEnter={onSubmit}
        placeholder={t("tactic.codePlaceholder")}
      />
      <div>
        <Button
          onClick={onSubmit}
          variant="tertiary"
          label={t("common:apply")}
          large
        />
      </div>
    </>
  );
};

export const TacticMode = ({
  mode,
  chosenTactic,
  onClose,
  handleCopy,
  handleChangeFormation,
  handleChangeBuildUpStyle,
  handleChangeDefensiveApproach,
  handleChangeLineHeight,
  handleGetTacticFromCode,
  statusInsertTacticCode,
  desktopMode,
  formations,
}: Props) => {
  const [eaCode, setEaCode] = useState("");
  const [tacticErrorMessage, setTacticErrorMessage] = useState("");
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("squadBuilder");
  const navigate = useNavigate();

  const formationLabel = useSelector(
    getFormationsLabel(chosenTactic?.teamTactics?.formationId),
  );

  useEffect(() => {
    if (statusInsertTacticCode === "rejected") {
      setTacticErrorMessage(t("tactic.invalidCode"));
    } else {
      setTacticErrorMessage("");
    }
    if (statusInsertTacticCode === "fulfilled") {
      dispatch(
        addAlert({
          title: t("tactic.appliedTitle"),
          description: t("tactic.applied", { code: eaCode }),
          type: "success",
        }),
      );
      if (!desktopMode) {
        window.scrollTo(0, 104);
      }
      if (mode === "squad-builder") {
        dispatch(sbUpdateTacticInsertStatus("idle"));
      } else {
        dispatch(tacticsUpdateTacticInsertStatus("idle"));
      }
      setEaCode("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusInsertTacticCode, dispatch]);

  const tacticTitle =
    chosenTactic?.name ||
    (mode === "squad-builder"
      ? t("tactic.customName")
      : t("tactic.noneSelected"));

  // The imported active-squad tactic comes with an empty description (DEV-3636).
  // A custom tactic gets the linked text, also for squads saved with old copy.
  const tacticDescription =
    chosenTactic && !chosenTactic.description ? (
      t("tactics:importedSquadDescription")
    ) : mode === "squad-builder" &&
      chosenTactic?.name === t("tactic.customName") ? (
      <Trans
        t={t}
        i18nKey="tactic.customDescription"
        components={{
          tactics: (
            <Link
              to="/tactics"
              onClick={() => trackExplore()}
              className="text-primary-action underline"
            />
          ),
        }}
      />
    ) : (
      chosenTactic?.description
    );
  const tacticImg = (
    <img
      alt={tacticTitle}
      src={
        chosenTactic?.logoUrl ||
        `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/tactics/logos/preset2.png`
      }
      className="rounded-tl-lg  h-[92px] aspect-square"
    />
  );

  const panelHeader = (
    <PanelHeader
      title={t("panel.tacticTemplate")}
      onClose={onClose}
      noCross={!chosenTactic}
    />
  );

  return (
    <div className="flex flex-col w-full">
      {panelHeader}
      <PanelContainer>
        <div
          className={cn(
            "flex flex-col w-full mt-8 overflow-x-hidden overflow-y-auto gap-y-4",
            mode === "tactics-overview" && "pb-16",
          )}
        >
          <div
            className={cn(
              "flex flex-row justify-between gap-x-2 desktop:hidden",
            )}
          >
            <InsertCodeAndApply
              {...{
                eaCode,
                setEaCode,
                tacticErrorMessage,
                setTacticErrorMessage,
                handleGetTacticFromCode,
                updateTacticStatus: () =>
                  mode === "squad-builder"
                    ? dispatch(sbUpdateTacticInsertStatus("idle"))
                    : dispatch(tacticsUpdateTacticInsertStatus("idle")),
              }}
            />
          </div>
          {tacticErrorMessage && (
            <span className="flex pl-3 text-xs text-red-500 desktop:hidden">
              {tacticErrorMessage}
            </span>
          )}
          {chosenTactic && (
            <TacticInfo
              eaCode={chosenTactic?.eaCode}
              socials={chosenTactic?.socials || {}}
              name={tacticTitle}
              author={chosenTactic?.author}
              icon={tacticImg}
              tags={chosenTactic?.tags || []}
              description={tacticDescription}
              popularityScore={chosenTactic?.popularityScore || 0}
              handleCopy={handleCopy}
              noTactic={!chosenTactic}
              isSquadBuidler={mode === "squad-builder"}
            />
          )}
          {chosenTactic && (
            <TacticalPreset
              formationId={chosenTactic?.teamTactics?.formationId}
              formationLabel={formationLabel}
              buildUpStyle={chosenTactic?.teamTactics?.buildUpStyle}
              defensiveApproach={chosenTactic?.teamTactics?.defensiveApproach}
              lineHeight={chosenTactic?.teamTactics?.lineHeight}
              editable={mode === "squad-builder"}
              formations={formations}
              onChangeFormation={handleChangeFormation}
              onChangeBuildUpStyle={handleChangeBuildUpStyle}
              onChangeDefensiveApproach={handleChangeDefensiveApproach}
              onChangeLineHeight={handleChangeLineHeight}
            />
          )}
          {chosenTactic && (
            <TacticPlayerRoles
              roles={chosenTactic?.playerRoles}
              formationId={chosenTactic?.teamTactics?.formationId}
              isSquadBuilder={mode === "squad-builder"}
            />
          )}

          {(mode === "squad-builder" || !chosenTactic) && (
            <div className="flex flex-col mobile:px-2 sb-v2-lg:px-0 px-0 gap-y-4">
              {mode === "tactics-overview" && (
                <p className="text-sm text-gray-200">
                  {t("tactic.didYouKnow")}
                </p>
              )}
              <div className="flex-row hidden w-full gap-x-2 desktop:flex">
                <InsertCodeAndApply
                  {...{
                    eaCode,
                    setEaCode,
                    tacticErrorMessage,
                    setTacticErrorMessage,
                    handleGetTacticFromCode,
                    updateTacticStatus: () =>
                      mode === "squad-builder"
                        ? dispatch(sbUpdateTacticInsertStatus("idle"))
                        : dispatch(tacticsUpdateTacticInsertStatus("idle")),
                  }}
                />
              </div>
              {tacticErrorMessage && (
                <span className="hidden pl-3 text-xs desktop:flex text-red-500">
                  {tacticErrorMessage}
                </span>
              )}
            </div>
          )}
          <div className="mx-auto pb-12">
            <Button
              onClick={() => {
                trackExplore();
                navigate("/tactics");
              }}
              variant="tertiary"
              large
              label={t("tactic.browseTactics")}
            />
          </div>
        </div>
      </PanelContainer>
    </div>
  );
};
